import { useState, useEffect } from "react";
import config from "config/config";
import { v4 as uuidv4 } from "uuid";
import { useForm } from "react-hook-form";
import { stringFormatingToPascal } from "js/formatting";
import { validateAgentInODS } from "service/userservice/validateAgentInODS";
import { useSearchParams } from "react-router-dom";
import { isFlagEnabled } from "js/utilities";

import Tooltip from "components/General/Tooltip";
import FormInputError from "components/General/FormInputError";
import DomainErrorModal from "components/ModalDialogs/DomainErrorModal";
import SSNInput from "components/General/Form/SSNInput";
import MaskedPhoneNumberInput from "components/General/Form/MaskedPhoneNumberInput";
import AgentRegistrationHelpTooltip from "components/AgentPortal/Registration/AgentRegistrationHelpTooltip";
import { useTranslation } from "react-i18next";

const StepOne = ({
  agentRegistrationFormData,
  setAgentRegistrationFormData,
  setCurrentStep,
  setRoleId,
  setLoading,
  setStepOneTitle,
}) => {
  const [searchParams] = useSearchParams();
  const uuid = uuidv4();
  const { t } = useTranslation();
  const [errorInfo, seterrorInfo] = useState("");
  const [modalState, setModalState] = useState(false);
  const [agentType, setAgentType] = useState(
    agentRegistrationFormData.accountType === undefined
      ? "agent"
      : agentRegistrationFormData.accountType
  );
  const onOptionChange = (e) => setAgentType(e.target.value);

  useEffect(() => {
    if (
      searchParams &&
      searchParams.get("accounttype") &&
      isFlagEnabled("ENABLE_AGENT_ASSISTANT_AND_LSP_REGISTRATION")
    ) {
      setAgentType(searchParams.get("accounttype").toLocaleLowerCase());
      setStepOneTitle(t("AgentRegister-step1-title-agent-assistant"));
    }
  }, [searchParams]);

  const {
    register: registrationStepOneRegister,
    handleSubmit,
    setValue: registrationStepOneSetValue,
    formState: { errors: registrationStepOneErrors },
  } = useForm({
    defaultValues: agentRegistrationFormData,
  });

  async function onSubmit(data) {
    if (
      window.localStorage.getItem("AgentRegCount") &&
      parseInt(window.localStorage.getItem("AgentRegCount")) >=
        parseInt(config.REGISTRATION_COUNTER) &&
      Date.now() - window.localStorage.getItem("AgentRegCounterDate") <
        24 * 60 * 60 * 1000
    ) {
      seterrorInfo(t("AgentRegister-step1-formError-1"));
      setModalState(true);
    } else {
      data.firstname = stringFormatingToPascal(data.firstname);
      data.lastname = stringFormatingToPascal(data.lastname);
      setLoading(true);

      let result = await validateAgentInODS(
        searchParams.get("accounttype")
          ? searchParams.get("accounttype")
          : "agent",
        data.agentId?.toUpperCase().trim(),
        data.firstname?.trim(),
        data.lastname?.trim(),
        data.ssn,
        data.zipCode?.trim(),
        data.phoneNumber?.trim(),
        data.email?.trim(),
        searchParams.get("userid") ? searchParams.get("userid") : ""
      ).catch((error) => {
        console.log(error);
      });

      setLoading(false);
      if (result && result?.status === undefined) {
        window.localStorage.removeItem("AgentRegCount");
        window.localStorage.removeItem("AgentRegCounterDate");
        let agent = result;
        data.email = agent.emailId;
        data.confirmEmail = agent.emailId;
        data.agentIdNo = agent.agentId;
        data.alternateId = agent.alternateId;
        data.identityNumber = agent.identityNumber;
        setRoleId(agent.roleId);
        setAgentRegistrationFormData((curr) => ({ ...curr, ...data }));
        setCurrentStep(2);
      } else {
        if (window.localStorage.getItem("AgentRegCount")) {
          if (
            Date.now() - window.localStorage.getItem("AgentRegCounterDate") >
            24 * 60 * 60 * 1000
          ) {
            window.localStorage.setItem("AgentRegCounterDate", Date.now());
            window.localStorage.setItem("AgentRegCount", 1);
          } else {
            window.localStorage.setItem(
              "AgentRegCount",
              parseInt(window.localStorage.getItem("AgentRegCount")) + 1
            );
          }
        } else {
          window.localStorage.setItem("AgentRegCounterDate", Date.now());
          window.localStorage.setItem("AgentRegCount", 1);
        }
        seterrorInfo(
          t("AgentRegister-error-message", {
            errorTitle: result?.title ?? "",
            email: config.AGENT_SERVICE_EMAIL,
          })
        );
        setModalState(true);
      }
    }
  }
  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      aria-label="agent registration form"
    >
      {agentType === "agent-administrative-assistant" && (
        <>
          <p className="mb-6 font-semibold text-base">
            {t("AgentRegister-step1-helpText-1")}
          </p>
        </>
      )}

      {agentType === "agent" && (
        <>
          <div>
            <label
              id={`${uuid}-agent-portal-id`}
              className="text-base font-semibold"
            >
              {t("AgentRegister-step1-agent-id-number")}
              <Tooltip
                srText={t("AgentRegister-step1-agent-tooltip-helpText")}
                content={<AgentRegistrationHelpTooltip />}
              />
            </label>
            <p className="text-xs mb-1">
              {t("AgentRegister-step1-helpText-3")}
            </p>
            <input
              id={`${uuid}-agentId`}
              className="w-full"
              type="text"
              placeholder="#"
              {...registrationStepOneRegister("agentId", {
                required: t("AgentRegister-step1-required-error-message"),
              })}
              aria-labelledby={`${uuid}-agent-portal-id`}
            />

            {registrationStepOneErrors.agentId && (
              <FormInputError
                id={`${uuid}-agentId-error`}
                errorMessage={registrationStepOneErrors.agentId.message}
              />
            )}
          </div>
          <hr />
        </>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
        <div>
          <label id={`${uuid}-firstname-label`}>
            {t("AgentRegister-step1-fn")}
          </label>
          <input
            id={`${uuid}-firstname`}
            className="w-full"
            type="text"
            {...registrationStepOneRegister("firstname", {
              required: t("AgentRegister-step1-fn-requiredText"),
              maxLength: {
                value: 50,
                message: t("AgentRegister-step1-fn-maxLengthMessage"),
              },
              pattern: {
                value: /^[A-Za-z-' ]{1,50}$/,
                message: t("AgentRegister-step1-fn-patternMessage"),
              },
            })}
            aria-labelledby={`${uuid}-firstname-label`}
          />
          {registrationStepOneErrors.firstname && (
            <FormInputError
              id={`${uuid}-firstname-error`}
              errorMessage={registrationStepOneErrors.firstname.message}
            />
          )}
        </div>

        <div>
          <label id={`${uuid}-lastname-label`}>
            {t("AgentRegister-step1-ln")}
          </label>
          <input
            id={`${uuid}-lastname`}
            className="w-full"
            type="text"
            {...registrationStepOneRegister("lastname", {
              required: t("AgentRegister-step1-ln-requiredText"),
              maxLength: {
                value: 50,
                message: t("AgentRegister-step1-ln-maxLengthMessage"),
              },
              pattern: {
                value: /^[A-Za-z-' ]{1,50}$/,
                message: t("AgentRegister-step1-ln-patternMessage"),
              },
            })}
            aria-labelledby={`${uuid}-lastname-label`}
          />
          {registrationStepOneErrors.lastname && (
            <FormInputError
              id={`${uuid}-lastname-error`}
              errorMessage={registrationStepOneErrors.lastname.message}
            />
          )}
        </div>

        <div>
          <label id={`${uuid}-mi-label`}>{t("AgentRegister-step1-mn")}</label>
          <input
            id={`${uuid}-mi`}
            className="w-full"
            type="text"
            {...registrationStepOneRegister("middleInitial", {
              pattern: {
                value: /^[A-Za-z]{1}$/,
                message: t("AgentRegister-step1-mn-patternMessage"),
              },
            })}
            aria-labelledby={`${uuid}-mi-label`}
          />
          {registrationStepOneErrors.middleInitial && (
            <FormInputError
              id={`${uuid}-mi-error`}
              errorMessage={registrationStepOneErrors.middleInitial.message}
            />
          )}
        </div>
        {agentType === "agent" && (
          <>
            <div className="relative">
              <label id={`${uuid}-ssn-label`}>
                {t("AgentRegister-step1-ssn")}
              </label>

              <SSNInput
                name="ssn"
                describedby={`${uuid}-ssn-label`}
                register={registrationStepOneRegister}
                setValue={registrationStepOneSetValue}
              />

              <div
                id={`${uuid}-ssnnote`}
                className="text-xxs text-theme-text-tertiary mt-1"
              >
                {t("AgentRegister-step1-ssn-helpText")}
              </div>
              {registrationStepOneErrors.ssn && (
                <FormInputError
                  id={`${uuid}-ssn-error`}
                  errorMessage={registrationStepOneErrors.ssn.message}
                />
              )}
            </div>
          </>
        )}

        {agentType === "agent-administrative-assistant" && (
          <>
            <div>
              <label id={`${uuid}-zipcode-label`}>
                {t("AgentRegister-step1-business-zipCode")}
              </label>
              <input
                id={`${uuid}-zipcode`}
                className="w-full"
                type="text"
                {...registrationStepOneRegister("zipCode", {
                  required: {
                    value: true,
                    message: t("AgentRegister-step1-zipCode-requiredText"),
                  },
                  pattern: {
                    value: /^\d{5}$/,
                    message: t(
                      "AgentRegister-step1-business-zipCode-patternMessage"
                    ),
                  },
                  maxLength: {
                    value: 5,
                    message: t(
                      "AgentRegister-step1-business-zipCode-maxLengthMessage"
                    ),
                  },
                })}
                aria-labelledby={`${uuid}-zipcode-label`}
              />
              {registrationStepOneErrors.zipCode && (
                <FormInputError
                  id={`${uuid}-zipcode-error`}
                  errorMessage={registrationStepOneErrors.zipCode.message}
                />
              )}
            </div>
            <div>
              <label id={`${uuid}-emailLabel`}>
                {t("AgentRegister-step1-email")}
              </label>
              <input
                id={`${uuid}-email`}
                className="w-full"
                type="text"
                {...registrationStepOneRegister("email", {
                  required: t("AgentRegister-step1-email-requiredText"),
                  pattern: {
                    value: /^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/,
                    message: t("AgentRegister-step1-email-patternMessage"),
                  },
                  validate: (value) => {
                    let domainList = config.ALLOWED_EMAIL_DOMAINLIST.split(";");
                    if (domainList.length > 0) {
                      if (
                        !domainList.some(
                          (x) =>
                            x.toLowerCase() ===
                            value.split("@")[1].toLowerCase()
                        )
                      )
                        return t("AgentRegister-step1-email-validateMessage");
                    }
                  },
                })}
                aria-labelledby={`${uuid}-emailLabel`}
              />

              {registrationStepOneErrors.email && (
                <FormInputError
                  id={`${uuid}-emailError`}
                  errorMessage={registrationStepOneErrors.email.message}
                />
              )}
            </div>
          </>
        )}

        <div>
          <label id={`${uuid}-phone-label`}>
            {t("AgentRegister-step1-mobilePhone")}
          </label>

          <MaskedPhoneNumberInput
            name="phoneNumber"
            describedby={`${uuid}-phone-label`}
            register={registrationStepOneRegister}
            setValue={registrationStepOneSetValue}
            required={false}
          />

          {registrationStepOneErrors.phoneNumber && (
            <FormInputError
              id={`${uuid}-phone-error`}
              errorMessage={registrationStepOneErrors.phoneNumber.message}
            />
          )}
          <span id={`${uuid}-phone-format`} className="sr-only">
            {t("AgentRegister-step1-phoneNumberFormat")}
          </span>
        </div>
      </div>

      <div className="mt-4 text-right">
        <button className="ui-btn-primary ui-btn-min-w mt-2">
          {t("AgentRegister-step1-submitButton")}
          <span className="sr-only">
            {t("AgentRegister-step1-submitButton-srText")}
          </span>
        </button>
      </div>
      <DomainErrorModal
        modalState={modalState}
        setModalState={setModalState}
        errorInfo={errorInfo}
      />
    </form>
  );
};

export default StepOne;
